/**
 * Panel plumbing: subscribe to store slices, rebuild the subtree, clean up on dispose.
 *
 * Two rules drive the design:
 *  - store listeners run *synchronously* inside the dispatching action, so a rebuild is deferred
 *    to a microtask. Rebuilding in-place would rip the element that is currently dispatching an
 *    event (a clicked button, a changed input) out of the DOM mid-dispatch.
 *  - several slice changes in one action collapse into a single rebuild.
 */

import type { EditorContext, EditorEvent } from '../context';
import type { EditorState, Store } from '../index';
import { dialogs } from '../dialogs';
import { el, icon, ICONS, type Child, type IconName } from './dom';

export interface PanelHandle {
  dispose(): void;
}

export interface PanelInstance extends PanelHandle {
  readonly root: HTMLElement;
  /** Queue a rebuild; repeated calls in one turn collapse into one. */
  schedule(): void;
  /** Register a cleanup callback run on dispose (extra subscriptions, timers). */
  own(cleanup: () => void): void;
}

export interface PanelOptions {
  className?: string;
  /** Rebuild when any of these slices changes identity (`Object.is`). */
  slices?: Array<(state: EditorState) => unknown>;
  /** Rebuild on these editor events (session-level changes the store cannot express). */
  events?: readonly EditorEvent[];
  /**
   * `'fill'` = this panel takes the column's leftover height (its `.sec.fill` scrolls inside it).
   * A dataset flag and not a class: the root className is exactly `panel <name>` by contract.
   */
  layout?: 'fill';
}

export function createPanel(
  container: HTMLElement,
  ctx: EditorContext,
  render: (root: HTMLElement) => void,
  options: PanelOptions = {},
): PanelInstance {
  const root = el('div', { class: `panel ${options.className ?? ''}`.trim() });
  if (options.layout) root.dataset.layout = options.layout;
  container.appendChild(root);

  const cleanups: Array<() => void> = [];
  let disposed = false;
  let queued = false;

  const draw = (): void => {
    root.replaceChildren();
    render(root);
  };

  const schedule = (): void => {
    if (disposed || queued) return;
    queued = true;
    queueMicrotask(() => {
      queued = false;
      if (!disposed) draw();
    });
  };

  for (const slice of options.slices ?? []) cleanups.push(ctx.store.select(slice, schedule));
  for (const event of options.events ?? []) cleanups.push(ctx.events.on(event, schedule));

  draw();

  return {
    root,
    schedule,
    own(cleanup) {
      cleanups.push(cleanup);
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      for (const cleanup of cleanups.splice(0)) cleanup();
      root.remove();
    },
  };
}

/**
 * Fire an async controller call from a DOM handler. Controllers already toast their failures, so
 * the only job here is to make sure a rejected promise never becomes an unhandled rejection.
 */
export function runAsync<T>(promise: Promise<T>, onDone?: (value: T) => void): void {
  promise.then(
    (value) => {
      onDone?.(value);
    },
    () => {
      /* already reported by the controller */
    },
  );
}

/**
 * Which section titles the user has collapsed. Module-level on purpose: panels rebuild their whole
 * subtree from state, so a collapse kept on the element would be undone by the next store change.
 */
const collapsedSections = new Set<string>();

/**
 * Which sections the user has clicked at least once. `defaultCollapsed` is a *suggestion for the
 * first look*, so it has to stop applying the moment the user has an opinion — and "the user
 * expanded it" is not expressible in `collapsedSections` (absence already means expanded).
 */
const touchedSections = new Set<string>();

export interface SectionOptions {
  /** Right-aligned meta on the title bar (joint name, count, …). */
  meta?: string;
  /** Leading icon; the collapse chevron keeps its place either way. */
  icon?: IconName;
  /** Drop the body padding — for full-bleed lists (the bone tree). */
  flush?: boolean;
  /** Collapse identity when two panels would otherwise share a title. Defaults to `title`. */
  key?: string;
  /** This block takes the leftover height of a `layout: 'fill'` panel and scrolls its body. */
  fill?: boolean;
  /** Start collapsed — until the user toggles this section once, after which they decide. */
  defaultCollapsed?: boolean;
}

/**
 * A titled, collapsible panel block. Every panel is a stack of these.
 *
 * Structure is the design contract (`design/ui-design.md` §4): `.sec > h2` title bar over a
 * `.pbody`. The class names are the ones the tests already select on (`.sec`, and section order
 * via `.sec:nth-of-type(n)`), so the shape below is deliberately additive.
 */
export function section(title: string, children: Child[], options: SectionOptions = {}): HTMLElement {
  const key = options.key ?? title;
  const startCollapsed =
    collapsedSections.has(key) || (options.defaultCollapsed === true && !touchedSections.has(key));

  const chevron = icon(startCollapsed ? 'chevronRight' : 'chevronDown', 12);
  const head = el('h2', { title: 'Click to collapse' }, [
    chevron,
    options.icon ? icon(options.icon, 12) : null,
    title,
    options.meta ? el('em', { text: options.meta }) : null,
  ]);
  const body = el('div', { class: options.flush ? 'pbody flush' : 'pbody' }, children);
  const classes = ['sec'];
  if (options.fill) classes.push('fill');
  if (startCollapsed) classes.push('collapsed');
  const root = el('section', { class: classes.join(' ') }, [head, body]);

  head.addEventListener('click', () => {
    touchedSections.add(key);
    const collapsed = !root.classList.contains('collapsed');
    root.classList.toggle('collapsed', collapsed);
    if (collapsed) collapsedSections.add(key);
    else collapsedSections.delete(key);
    chevron.innerHTML = ICONS[collapsed ? 'chevronRight' : 'chevronDown'];
  });

  return root;
}

/** CSS px of pointer travel per `step` while scrubbing a numeric slot. */
const SCRUB_PX_PER_STEP = 4;

/**
 * Drag-to-scrub, the second half of design rule 4: a numeric slot is not just a label and a value,
 * it is a horizontal drag target.
 *
 * Two decisions worth keeping:
 *  - a press that lands *on the input* is left alone, so clicking in to type still works;
 *  - the drag rewrites `input.value` for feedback but fires exactly one `change` on release, so a
 *    scrub is one store dispatch and one undo entry, not one per pointer move.
 */
function attachScrub(slot: HTMLElement, input: HTMLInputElement): void {
  const step = Number(input.step) || 1;
  let startX = 0;
  let startValue = 0;
  let scrubbing = false;
  // `step` can be fractional (0.005); round the result to its decimals or floats drift visibly.
  const decimals = (String(step).split('.')[1] ?? '').length;

  slot.addEventListener('pointerdown', (event) => {
    if (input.disabled || event.button !== 0 || event.target === input) return;
    const value = Number(input.value);
    if (!Number.isFinite(value)) return;
    scrubbing = true;
    startX = event.clientX;
    startValue = value;
    slot.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  });

  slot.addEventListener('pointermove', (event) => {
    if (!scrubbing) return;
    const steps = Math.round((event.clientX - startX) / SCRUB_PX_PER_STEP);
    input.value = (startValue + steps * step).toFixed(decimals);
  });

  const end = (event: PointerEvent): void => {
    if (!scrubbing) return;
    scrubbing = false;
    slot.releasePointerCapture?.(event.pointerId);
    if (Number(input.value) !== startValue) input.dispatchEvent(new Event('change', { bubbles: true }));
  };
  slot.addEventListener('pointerup', end);
  slot.addEventListener('pointercancel', end);
}

/**
 * One numeric/label line — the signature of the language (design rule 4): label left, value right,
 * a single filled slot. The CSS does the work, so every existing `field()` call site becomes a
 * slot without changing.
 */
export function field(label: string, ...controls: Child[]): HTMLElement {
  const slot = el('div', { class: 'field' }, [
    el('span', { class: 'flabel', text: label }),
    ...controls,
  ]);
  const input = slot.querySelector<HTMLInputElement>('input[type="number"]');
  if (input) {
    slot.classList.add('scrub');
    attachScrub(slot, input);
  }
  return slot;
}

/** A read-only slot: recessed instead of raised, so it does not read as draggable. */
export function readonlyField(label: string, value: string): HTMLElement {
  return el('div', { class: 'field ro' }, [
    el('span', { class: 'flabel', text: label }),
    el('span', { class: 'readout', text: value }),
  ]);
}

/** Same slot as `readonlyField`, under the name the newer panels read better with. */
export function readout(label: string, value: string): HTMLElement {
  return readonlyField(label, value);
}

/**
 * A row's actions, revealed on hover/selection (CSS `.row .tools`). Keeping them out of the flow
 * until then is what lets a 280px column carry four actions per row without wrapping.
 */
export function toolsCell(children: Child[]): HTMLElement {
  return el('span', { class: 'tools' }, children);
}

export function note(text: string, className = 'note'): HTMLElement {
  return el('p', { class: className, text });
}

/**
 * The ONE wording of "you are about to lose the clip you are editing". Every panel that replaces
 * the current clip (new, load, switch project) asks through here, so the question cannot drift and
 * the controllers' own `window.confirm` fallback is never reached from the UI.
 */
export function confirmDiscardDirtyClip(store: Store): Promise<boolean> {
  const state = store.getState();
  if (!state.clipDirty) return Promise.resolve(true);
  return dialogs.confirm({
    title: 'Discard unsaved changes?',
    message: `"${state.clip?.name ?? 'clip'}" has unsaved changes. Discard them?`,
    confirmLabel: 'Discard',
    danger: true,
  });
}
